import { createReference, extractAccountReferences, getExtensionValue } from "@medplum/core";
import type { Provenance, ProvenanceAgent, Reference, Resource } from "@medplum/fhirtypes";

import { ProvenanceParticipantType } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import { joinReferenceString, setExtensionValue } from "@/utils/medplum";

export interface BuildVersionedTargetProvenanceParams {
  /** The resource as it exists after the write, carrying the version this Provenance describes. */
  resource: Resource;
  /** Extension URL stamped on the target with `meta.versionId`. */
  versionExtensionUrl: string;
  activity: {
    system: string;
    code: string;
    display: string;
  };
  /** Who made the change — the acting user's profile. */
  agentReference: ProvenanceAgent["who"];
  /**
   * Practice to account the Provenance to when the written resource does not yet carry accounts
   * (a create that stamps `$set-accounts` after the audit record).
   */
  practiceOrganizationId?: string;
}

const isOrganizationReference = (reference: Reference): boolean =>
  Boolean(reference.reference?.startsWith("Organization/"));

/**
 * Organization accounts the Provenance should live under: an explicit practice id, else the
 * Organization refs already on the written resource.
 */
function provenanceOrganizationAccounts(
  resource: Resource,
  practiceOrganizationId: string | undefined,
): Reference[] | undefined {
  const trimmedPracticeOrganizationId = practiceOrganizationId?.trim();
  if (trimmedPracticeOrganizationId) {
    return [{ reference: joinReferenceString("Organization", trimmedPracticeOrganizationId) }];
  }

  const fromResource = (extractAccountReferences(resource.meta) ?? []).filter(
    isOrganizationReference,
  );
  return fromResource.length > 0 ? fromResource : undefined;
}

/**
 * Builds a Provenance that records one mutation of a versioned FHIR resource.
 *
 * The version the write produced rides on the target reference as an extension. `meta.versionId`
 * moves with every write, so a plain reference would resolve to the current resource rather than
 * the one this record describes. With the version stamped, the reader hands it to
 * `medplum.readVersion` and gets the resource as it stood.
 *
 * Practice membership is copied onto `meta.accounts` so a later `_compartment` search finds the
 * record under the resource's practice, not only under whoever wrote it.
 *
 * @param resource The resource after the write, whose `meta.versionId` is stamped on the target.
 * @param versionExtensionUrl The target extension that carries that version id.
 * @param activity The mutation being recorded.
 * @param agentReference The acting user, recorded as the performer.
 * @returns The Provenance to create. Returns it unsaved so the caller owns the write and its failure.
 */
export function buildVersionedTargetProvenance({
  resource,
  versionExtensionUrl,
  activity,
  agentReference,
  practiceOrganizationId,
}: BuildVersionedTargetProvenanceParams): Provenance {
  const recorded = new Date().toISOString();
  const target = createReference(resource);
  const versionId = resource.meta?.versionId;
  const accounts = provenanceOrganizationAccounts(resource, practiceOrganizationId);

  return {
    resourceType: "Provenance",
    ...(accounts ? { meta: { accounts } } : {}),
    target: [
      versionId
        ? {
            ...target,
            extension: setExtensionValue(undefined, versionExtensionUrl, {
              valueString: versionId,
            }),
          }
        : target,
    ],
    recorded,
    occurredDateTime: recorded,
    activity: {
      coding: [
        {
          system: activity.system,
          code: activity.code,
          display: activity.display,
        },
      ],
    },
    agent: [
      {
        type: {
          coding: [
            {
              system: Systems.HL7ProvenanceParticipantType,
              code: ProvenanceParticipantType.Performer,
              display: "Performer",
            },
          ],
        },
        who: agentReference,
      },
    ],
  };
}

/**
 * Reads the resource version id stamped on a Provenance's target.
 *
 * @param provenance The Provenance to read, if there is one.
 * @param versionExtensionUrl The target extension that carries the version id.
 * @returns The stamped version id, or undefined when the record predates the extension.
 */
export function getProvenanceTargetVersionId(
  provenance: Provenance | undefined,
  versionExtensionUrl: string,
): string | undefined {
  const target = provenance?.target?.[0];
  if (!target) return undefined;

  const versionId = getExtensionValue(target, versionExtensionUrl);
  return typeof versionId === "string" && versionId.length > 0 ? versionId : undefined;
}

/**
 * Reads the mutation a Provenance records for one activity CodeSystem.
 *
 * @param provenance The Provenance to read, if there is one.
 * @param activitySystem The CodeSystem whose codes describe this feature's mutations.
 * @param allowedCodes The codes that system may record.
 * @returns The activity code, or undefined when the coding is absent or from another system.
 */
export function getProvenanceActivityCode<ActivityCode extends string>(
  provenance: Provenance | undefined,
  activitySystem: string,
  allowedCodes: readonly ActivityCode[],
): ActivityCode | undefined {
  const coding = provenance?.activity?.coding?.find(
    (candidate) => candidate.system === activitySystem,
  );
  const code = coding?.code;

  return allowedCodes.find((activity) => activity === code);
}
